import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, CheckSquare, Clock, Handshake, Loader2, Search, Target, X } from 'lucide-react';
import {
  createBlock, createCalendarTask, createMeeting, searchDeals, type DealOption, type Host,
} from '@/api/calendar';
import { getQuickTasks, type QuickTaskPreset } from '@/api/crmCard';
import { Sheet } from '@/components/Sheet';
import { calKeys } from '@/hooks/useCalendarData';
import { useProject } from '@/contexts/ProjectProvider';
import { addDays, dayKey, momentOf } from '@/lib/calendarDates';
import { useToast } from '@/store/toast';

/** Что добавить (прототип: newSheet для календаря). */
export function ChooseSheet({ onPick, onClose }: { onPick: (k: 'task' | 'meeting' | 'block') => void; onClose: () => void }) {
  const items: Array<{ k: 'task' | 'meeting' | 'block'; icon: typeof CheckSquare; t: string; s: string }> = [
    { k: 'task', icon: CheckSquare, t: 'Быстрая задача', s: 'Не занимает время, можно с напоминанием' },
    { k: 'meeting', icon: CalendarDays, t: 'Встреча с клиентом', s: 'Занимает время в календаре' },
    { k: 'block', icon: Clock, t: 'Занять личное время', s: 'Чтобы на это время не назначали встречи' },
  ];
  return (
    <Sheet label="Добавить в календарь" onClose={onClose}>
      <div className="sh"><h3>Добавить в календарь</h3></div>
      <div className="sb" style={{ gap: 2 }}>
        {items.map(({ k, icon: Icon, t, s }) => (
          <button key={k} type="button" className="opt" onClick={() => onPick(k)}>
            <Icon aria-hidden /><span className="t">{t}<small>{s}</small></span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

/** Выбор лида или сделки поиском (как LeadPicker веб-CRM: от 2 букв, 8 вариантов). */
export function LeadPicker({ value, onChange }: { value: DealOption | null; onChange: (d: DealOption | null) => void }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<DealOption[]>([]);
  const [loading, setLoading] = useState(false);
  const req = useRef(0);
  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setItems([]); return; }
    const my = ++req.current;
    const t = setTimeout(() => {
      setLoading(true);
      searchDeals(text)
        .then((r) => { if (my === req.current) setItems(r); })
        .catch(() => { if (my === req.current) setItems([]); })
        .finally(() => { if (my === req.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  if (value) {
    const Icon = value.isLead ? Target : Handshake;
    return (
      <div className="picked-deal">
        <Icon size={16} style={{ color: 'var(--accent)' }} aria-hidden />
        <span>{value.title}<small>{value.isLead ? 'Лид' : 'Сделка'}{value.personName ? ` · ${value.personName}` : ''}</small></span>
        <button type="button" aria-label="Убрать привязку" onClick={() => onChange(null)}><X size={16} aria-hidden /></button>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label className="ifield">
        Лид или сделка (необязательно)
        <span style={{ position: 'relative' }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Название, имя или телефон" aria-label="Поиск лида или сделки" />
          <span style={{ position: 'absolute', right: 12, top: 12, color: 'var(--faint)' }}>{loading ? <Loader2 size={18} className="spin-i" aria-hidden /> : <Search size={18} aria-hidden />}</span>
        </span>
      </label>
      {items.map((d) => (
        <button key={d.id} type="button" className="opt" onClick={() => { onChange(d); setQ(''); setItems([]); }}>
          {d.isLead ? <Target aria-hidden /> : <Handshake aria-hidden />}
          <span className="t">{d.title}<small>{d.isLead ? 'Лид' : 'Сделка'}{d.personName ? ` · ${d.personName}` : ''}</small></span>
        </button>
      ))}
      {q.trim().length >= 2 && !loading && items.length === 0 && <span className="pnote">Ничего не нашлось</span>}
    </div>
  );
}

const REMIND = [
  { v: '', label: 'Не напоминать' }, { v: '0', label: 'В момент задачи' }, { v: '15', label: 'За 15 минут' },
  { v: '60', label: 'За час' }, { v: '1440', label: 'За день' },
];
const REPEAT = [
  { v: '', label: 'Не повторять' }, { v: 'daily', label: 'Каждый день' }, { v: 'weekdays', label: 'По будням' },
  { v: 'weekly', label: 'Каждую неделю' }, { v: 'monthly', label: 'Каждый месяц' },
];

/** Сохранили — перечитать календарь и счётчики. */
function useCalendarRefresh() {
  const qc = useQueryClient();
  const { projectId } = useProject();
  return () => void qc.invalidateQueries({ queryKey: calKeys.all(projectId) });
}

/**
 * Быстрая задача (поля — как QuickTaskModal веб-CRM): что сделать (+ заготовки CRM), день, время,
 * напомнить (только со временем — без него сервер напоминание снимет), повтор, кому, лид или сделка.
 */
export function TaskSheet({ day, hosts, canAssign, onClose }: { day: string; hosts: Host[]; canAssign: boolean; onClose: () => void }) {
  const { projectId } = useProject();
  const refresh = useCalendarRefresh();
  const toast = useToast((t) => t.show);
  const presets = useQuery({ queryKey: calKeys.quick(projectId), queryFn: () => getQuickTasks(null), staleTime: 10 * 60_000 });
  const [preset, setPreset] = useState<QuickTaskPreset | null>(null);
  const [title, setTitle] = useState('');
  const [d, setD] = useState(day);
  const [time, setTime] = useState('');
  const [remind, setRemind] = useState('');
  const [repeat, setRepeat] = useState('');
  const [assignee, setAssignee] = useState('');
  const [deal, setDeal] = useState<DealOption | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = title.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(d);
  const people = canAssign ? hosts.filter((h) => !h.me) : [];

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true); setError(null);
    try {
      const startsAt = time ? momentOf(d, time) : null;
      await createCalendarTask({
        title: title.trim(), dueDate: d, startsAt,
        remindBeforeMin: startsAt && remind !== '' ? Number(remind) : null,
        repeat: repeat || null,
        ...(assignee ? { assigneeUserId: assignee } : {}),
        ...(deal ? { entityType: 'deal' as const, entityId: deal.id } : {}),
        ...(preset && preset.title === title.trim() ? { templateKey: preset.key, color: preset.color ?? null } : {}),
      });
      refresh();
      toast('Быстрая задача создана');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить задачу');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet label="Быстрая задача" onClose={onClose} tall>
      <div className="sh"><h3>Быстрая задача</h3></div>
      <div className="sb">
        <p className="pnote" style={{ margin: 0 }}>Время не занимает — встречу на это же время поставить можно.</p>
        <label className="ifield">Что сделать
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Напр. Перезвонить и уточнить объём" />
        </label>
        {!!presets.data?.length && (
          <div className="chips" role="group" aria-label="Заготовки">
            {presets.data.map((p) => (
              <button key={p.key} type="button" className={`chip${preset?.key === p.key ? ' on' : ''}`} aria-pressed={preset?.key === p.key}
                onClick={() => { setPreset(p); setTitle(p.title); }}>{p.title}</button>
            ))}
          </div>
        )}
        <div className="frow">
          <label className="ifield">День<input type="date" value={d} onChange={(e) => setD(e.target.value)} /></label>
          <label className="ifield">Время (необяз.)<input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></label>
        </div>
        <div className="frow">
          <label className="ifield">Напомнить
            <select value={time ? remind : ''} disabled={!time} onChange={(e) => setRemind(e.target.value)}>
              {REMIND.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </label>
          <label className="ifield">Повтор
            <select value={repeat} onChange={(e) => setRepeat(e.target.value)}>
              {REPEAT.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </label>
        </div>
        {people.length > 0 && (
          <label className="ifield">Кому — в чей календарь
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Мне</option>
              {people.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </label>
        )}
        <LeadPicker value={deal} onChange={setDeal} />
        {error && <p className="ferr" role="alert">{error}</p>}
        <div className="btnrow">
          <button type="button" className="primary" disabled={!valid || busy} onClick={() => void save()}>
            {busy && <Loader2 className="spin-i" aria-hidden />}Создать
          </button>
          <button type="button" className="ghost" onClick={onClose}>Отмена</button>
        </div>
      </div>
    </Sheet>
  );
}

const DURATIONS = [15, 30, 45, 60, 90];

/** Ближайший час — начало встречи по умолчанию. */
function nextHour(): { day: string; time: string } {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return { day: dayKey(d), time: `${String(d.getHours()).padStart(2, '0')}:00` };
}

/**
 * Встреча с клиентом (POST bookings — встреча, которую заводит сотрудник). Поля по минимуму:
 * тема, лид (или с кем), день, начало, длительность; приглашение клиенту — как в вебе, включено.
 */
export function MeetingSheet({ day, deal: initialDeal, defaultMin = 30, onClose }: {
  day?: string; deal?: DealOption | null; defaultMin?: number; onClose: () => void;
}) {
  const refresh = useCalendarRefresh();
  const toast = useToast((t) => t.show);
  const nh = nextHour();
  const [title, setTitle] = useState('');
  const [deal, setDeal] = useState<DealOption | null>(initialDeal ?? null);
  const [guest, setGuest] = useState('');
  const [d, setD] = useState(day && day !== nh.day ? day : nh.day);
  const [time, setTime] = useState(day && day !== nh.day ? '10:00' : nh.time);
  const [dur, setDur] = useState(DURATIONS.includes(defaultMin) ? defaultMin : 30);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(d) && /^\d{2}:\d{2}$/.test(time) && (!!deal || guest.trim().length > 0);

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true); setError(null);
    try {
      await createMeeting({
        title: title.trim() || undefined, startsAt: momentOf(d, time), durationMin: dur,
        ...(deal ? { dealId: deal.id } : { guestName: guest.trim() }), notifyClient: notify,
      });
      refresh();
      toast('Встреча добавлена в календарь');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось записать встречу');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet label="Встреча с клиентом" onClose={onClose} tall>
      <div className="sh"><h3>Встреча с клиентом</h3></div>
      <div className="sb">
        <label className="ifield">О чём встреча<input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Напр. Подписание договора" /></label>
        <LeadPicker value={deal} onChange={setDeal} />
        {!deal && <label className="ifield">С кем (если лида нет)<input value={guest} onChange={(e) => setGuest(e.target.value)} placeholder="Имя клиента" /></label>}
        <div className="frow">
          <label className="ifield">День<input type="date" value={d} onChange={(e) => setD(e.target.value)} /></label>
          <label className="ifield">Начало<input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></label>
        </div>
        <div className="chips" role="radiogroup" aria-label="Длительность">
          {DURATIONS.map((m) => (
            <button key={m} type="button" role="radio" aria-checked={dur === m} className={`chip${dur === m ? ' on' : ''}`} onClick={() => setDur(m)}>
              {m < 60 ? `${m} мин` : m === 60 ? '1 час' : '1,5 часа'}
            </button>
          ))}
        </div>
        <label className="toggle"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />Отправить клиенту приглашение</label>
        {error && <p className="ferr" role="alert">{error}</p>}
        <div className="btnrow">
          <button type="button" className="primary" disabled={!valid || busy} onClick={() => void save()}>
            {busy && <Loader2 className="spin-i" aria-hidden />}Записать
          </button>
          <button type="button" className="ghost" onClick={onClose}>Отмена</button>
        </div>
      </div>
    </Sheet>
  );
}

/** «Занять личное время» (POST bookings/blocks): название по желанию, день, с–по или весь день. */
export function BlockSheet({ day, onClose }: { day: string; onClose: () => void }) {
  const refresh = useCalendarRefresh();
  const toast = useToast((t) => t.show);
  const [title, setTitle] = useState('');
  const [d, setD] = useState(day);
  const [allDay, setAllDay] = useState(false);
  const [from, setFrom] = useState('13:00');
  const [to, setTo] = useState('14:00');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(d) && (allDay || from < to);

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true); setError(null);
    try {
      await createBlock(allDay
        ? { title: title.trim() || undefined, startsAt: momentOf(d, '00:00'), endsAt: momentOf(addDays(d, 1), '00:00'), allDay: true }
        : { title: title.trim() || undefined, startsAt: momentOf(d, from), endsAt: momentOf(d, to) });
      refresh();
      toast('Время отмечено как занятое');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отметить время');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet label="Занять личное время" onClose={onClose}>
      <div className="sh"><h3>Занять личное время</h3></div>
      <div className="sb">
        <label className="ifield">Название (необязательно)<input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Напр. Обед / Личные дела" /></label>
        <label className="ifield">День<input type="date" value={d} onChange={(e) => setD(e.target.value)} /></label>
        <label className="toggle"><input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />Весь день</label>
        {!allDay && (
          <div className="frow">
            <label className="ifield">С<input type="time" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label className="ifield">До<input type="time" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          </div>
        )}
        {error && <p className="ferr" role="alert">{error}</p>}
        <div className="btnrow">
          <button type="button" className="primary" disabled={!valid || busy} onClick={() => void save()}>
            {busy && <Loader2 className="spin-i" aria-hidden />}Занять
          </button>
          <button type="button" className="ghost" onClick={onClose}>Отмена</button>
        </div>
      </div>
    </Sheet>
  );
}
